import { useEffect, useRef } from 'react'

interface IntersectionObserverConfig extends IntersectionObserverInit {
  /**
   * Will disconnect the observer after it has been triggered once.
   */
  runOnce: boolean
}

interface CallbackPredicateVars {
  isIntersecting: boolean
  hasRunBefore: boolean
}

const always = () => true

const defaultConfig = {
  root: null,
  threshold: 0,
}

const useWhenVisible = <T extends HTMLElement>(
  callback: () => void,
  when: (params: CallbackPredicateVars) => boolean = always,
  observerConfig: Partial<IntersectionObserverConfig> = defaultConfig,
) => {
  const ref = useRef<T>(null)
  const hasRunBefore = useRef(false)

  const disconnectObserver = () => {
    if (observerConfig.runOnce) {
      observer.current.disconnect()
    }
  }

  const observer = useRef(
    new IntersectionObserver(
      ([{ isIntersecting }]) => {
        if (when({ isIntersecting, hasRunBefore: hasRunBefore.current })) {
          callback()
          hasRunBefore.current = true
          disconnectObserver()
        }
      },
      { ...defaultConfig, ...observerConfig },
    ),
  )

  useEffect(() => {
    const { current: currentObserver } = observer

    if (ref.current) {
      currentObserver?.observe(ref.current)
    }

    return () => currentObserver?.disconnect()
  }, [ref, observer])

  return ref
}

export default useWhenVisible
